/**
 * MUI Theme Variants
 *
 * Three MUI themes that share semantic intent but differ in palette/typography.
 * The actual color *values* live in tokens.css as CSS custom properties;
 * the MUI theme here mirrors those tokens so MUI components (Button, TextField,
 * Chip, etc.) receive the right palette without hardcoding hex codes at the call site.
 *
 * Note: OKLCH is evaluated by the browser, so we pass the literal `var(--…)` strings
 * where possible. For properties MUI needs at theme-build time (e.g. palette.main),
 * we duplicate the concrete value.
 */
import { createTheme, Theme } from '@mui/material/styles';

export type ThemeMode = 'light' | 'dark' | 'legacy';

const baseTypography = {
  // Korean+Latin unified stack (#298): Pretendard leads so 한·영 혼용 텍스트가
  // 같은 서체로 렌더된다. legacy 테마는 아래 자체 Courier 스택이라 영향 없음.
  fontFamily: [
    '"Pretendard Variable"',
    'Pretendard',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
};

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
    error: { main: '#d32f2f' },
    warning: { main: '#ed6c02' },
    success: { main: '#2e7d32' },
    info: { main: '#1976d2' },
    background: { default: '#fafbfc', paper: '#ffffff' },
    text: { primary: 'rgba(0, 0, 0, 0.87)', secondary: 'rgba(0, 0, 0, 0.6)' },
  },
  typography: baseTypography,
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#6ba7d4' },
    secondary: { main: '#cc82a9' },
    error: { main: '#d1514a' },
    warning: { main: '#db8e47' },
    success: { main: '#4bb38a' },
    info: { main: '#6ba7d4' },
    // `paper` at L≈0.20 (≈ #25282f) — balanced dark surface for
    // dialogs/menus/drawers. Raised above canvas (L=0.08) but subdued
    // enough for HMI aesthetic. Elevation overlay suppressed below
    // (MuiPaper) so this value renders as-is.
    background: { default: '#0d0e12', paper: '#25282f' },
    text: { primary: '#d4d6db', secondary: '#9ea2ac' },
    divider: 'rgba(255, 255, 255, 0.12)',
  },
  typography: {
    ...baseTypography,
    button: {
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontWeight: 500,
    },
  },
  shape: { borderRadius: 6 },
  components: {
    // Dark mode: MUI adds an elevation-based white overlay to Paper that lightens
    // the surface way above our defined `background.paper` value. Suppress it so
    // Paper renders at its exact hex, keeping the dialog/menu/drawer properly dark.
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

const legacyTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#0038b8' },
    secondary: { main: '#b00000' },
    error: { main: '#e60000' },
    warning: { main: '#ff6600' },
    success: { main: '#008000' },
    info: { main: '#0038b8' },
    // Beige-first: default is the base metal panel, paper is a slightly raised card.
    // Dark #2d2d2d is reserved for true canvas contexts (FlowCanvas) via --bg-canvas token.
    background: { default: '#b5b5ad', paper: '#c8c8c0' },
    text: { primary: '#101010', secondary: '#2a2a2a' },
    divider: '#808078',
    action: {
      active: '#101010',
      hover: 'rgba(0, 0, 0, 0.08)',
      selected: 'rgba(0, 0, 0, 0.16)',
      disabled: 'rgba(0, 0, 0, 0.30)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
    },
  },
  typography: {
    fontFamily: ['"Courier New"', 'Courier', '"Lucida Console"', 'monospace'].join(','),
    button: {
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontWeight: 700,
    },
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
  },
  shape: { borderRadius: 0 },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          boxShadow: 'inset 1px 1px 0 #e8e8e0, inset -1px -1px 0 #606058',
        },
        elevation1: {
          boxShadow: 'inset 1px 1px 0 #e8e8e0, inset -1px -1px 0 #606058',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'inset 1px 1px 0 #e8e8e0, inset -1px -1px 0 #606058, 0 1px 0 rgba(0,0,0,0.3)',
          border: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'inset -2px -2px 0 #606058, inset 2px 2px 0 #ffffff',
          borderRadius: 0,
          '&:active': {
            boxShadow: 'inset 2px 2px 0 #606058, inset -2px -2px 0 #ffffff',
          },
        },
        outlined: {
          borderWidth: 0,
          boxShadow: 'inset -2px -2px 0 #606058, inset 2px 2px 0 #ffffff',
          '&:hover': {
            borderWidth: 0,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
  },
});

export const MUI_THEMES: Record<ThemeMode, Theme> = {
  light: lightTheme,
  dark: darkTheme,
  legacy: legacyTheme,
};

export const THEME_LABELS: Record<ThemeMode, string> = {
  light: 'Light',
  dark: 'Dark HMI',
  legacy: 'Legacy',
};
